<template>
  <div class="product-card" :class="divClass">
    <div class="product-header">
      <div class="product-title">{{ props.product.titulo }}</div>
      <span class="status-tag" :class="statusTagClass">{{ productStatus }}</span>
    </div>
    
    <div class="product-date" v-if="props.product.fecha != ''">
      {{ props.product.hora }} &bull; {{ formatDate(props.product.fecha) }}
    </div>
    <div class="product-date" v-else-if="props.fecha_validez != null">
      Validez: {{ formatDate(props.fecha_validez)}}
    </div>

    <div class="product-footer">
      <div class="product-amounts">
        <span>Comprados: <strong>{{ props.product.cantidad }}</strong></span>
        <span style="margin-left: 12px;">Canjeados: <strong>{{ props.product.cantidad_canjeada }}</strong></span>
      </div>

      <div class="buttons-container" v-if="props.status === 'Pendiente' && (props.product.cantidad - props.product.cantidad_canjeada) > 0">
        <button type="button" class="qty-btn" :class="minusButtonClass" @click="decreaseQuantity" :disabled="cantidad === 0">&minus;</button>
        <span class="quantity">{{ cantidad }}</span>
        <button type="button" class="qty-btn" :class="plusButtonClass" @click="increaseQuantity" :disabled="cantidad >= (props.product.cantidad - props.product.cantidad_canjeada)">&plus;</button>
      </div>
    </div>
  </div>
</template>
  
<script setup>
  import { defineEmits, defineExpose, defineProps, ref, computed } from 'vue';
  import { format, parse } from 'date-fns';
  import { es } from 'date-fns/locale';
  
  const props = defineProps({
    status: String,
    fecha_validez: String,
    product: Object,
  });
  
  const productStatus = ref("");
  const cantidad = ref(0);

  if (props.product == null) {
    productStatus.value = "";
  } else if (props.product.cantidad == props.product.cantidad_canjeada) {
    productStatus.value = "Canjeado";
  } else if (props.product.cantidad_canjeada > 0) {
    productStatus.value = "Canjeado parcial.";
  } else if (props.status === 'Pendiente') {
    productStatus.value = "Vigente";
  } else if (props.status === 'Caducado') {
    productStatus.value = "Caducado";
  } else {
    productStatus.value = "";
  }

  const divClass = computed(() => {
    if (props.product.cantidad == props.product.cantidad_canjeada){
      return "redeemed";
    } else if (props.status === 'Pendiente'){
      return "pending";
    } else { //if (props.status === 'Caducado'){
      return "expired";
    }
  });

  const statusTagClass = computed(() => {
    if (props.product.cantidad == props.product.cantidad_canjeada){
      return "status-tag-redeemed";
    } else if (props.product.cantidad_canjeada > 0) {
      return "status-tag-partially-redeemed";
    } else if (props.status === 'Pendiente'){
      return "status-tag-pending";
    } else { //if (props.status === 'Caducado'){
      return "status-tag-expired";
    }
  })

  const minusButtonClass = computed(() => {
    if (cantidad.value == 0) {
      return "button-disabled";
    } else {
      return "button-enabled"
    }
  });

  const plusButtonClass = computed(() => {
    if (cantidad.value == props.product.cantidad - props.product.cantidad_canjeada) {
      return "button-disabled";
    } else {
      return "button-enabled"
    }
  });

  const emit = defineEmits(['update:quantity']);

  const decreaseQuantity = () => {
    if (cantidad.value > 0) {
      cantidad.value--;
      emit('update:quantity', cantidad.value);
    }
  }

  const increaseQuantity = () => {
    if (cantidad.value < props.product.cantidad - props.product.cantidad_canjeada && props.status === 'Pendiente') {
      cantidad.value++;
      emit('update:quantity', cantidad.value);
    }
  }
  
  const selectAllExecuted = () => {
    cantidad.value = props.product.cantidad - props.product.cantidad_canjeada;
    emit('update:quantity', cantidad.value);
  };

  defineExpose({ selectAllExecuted });

  const formatDate = (date) => {
    const fechaAux = parse(date, "yyyy-MM-dd", new Date());
    return format(fechaAux, "dd MMM yyyy", { locale: es });
  };


</script>
  
<style scoped>  
.product-card {
  border-radius: var(--radius-md);
  padding: 1rem 1.25rem;
  margin-bottom: 0.85rem;
  border: 1.5px solid var(--border-color);
  background-color: var(--white);
  transition: all var(--transition-fast);
}

.product-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
}

.product-title {
  font-weight: 700;
  font-size: 0.95rem;
  color: var(--text-color-dark);
}

.product-date {
  font-size: 0.825rem;
  color: var(--text-color-light);
  margin-bottom: 0.75rem;
}

.product-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px dashed var(--border-color-subtle);
}

.product-amounts {
  font-size: 0.825rem;
  color: var(--text-color-dark);
}

.pending {
  border-color: rgba(32, 205, 168, 0.4);
  background-color: rgba(32, 205, 168, 0.04);
}

.redeemed,
.expired {
  background-color: var(--very-clear-gray);
  border-color: var(--border-color);
  opacity: 0.75;
}

.status-tag {
  font-size: 11px;
  font-weight: 600;
  border-radius: var(--radius-full);
  padding: 3px 10px;
  white-space: nowrap;
}

.status-tag-pending {
  background-color: var(--status-success-bg);
  color: #0d8269;
}

.status-tag-redeemed {
  background-color: var(--very-clear-gray);
  color: var(--text-color-light);
}

.status-tag-partially-redeemed {
  background-color: var(--status-warning-bg);
  color: var(--status-warning-text);
}

.status-tag-expired {
  background-color: var(--status-error-bg);
  color: var(--status-error-text);
}

.buttons-container {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.qty-btn {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid var(--border-color);
  background: var(--white);
  color: var(--text-color-dark);
  font-size: 16px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all var(--transition-fast);
  padding: 0;
}

.qty-btn:hover:not(:disabled) {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.quantity {
  font-weight: 700;
  font-size: 1.1rem;
  color: var(--text-color-dark);
  min-width: 24px;
  text-align: center;
}

.button-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.button-enabled {
  opacity: 1;
}
</style>
  